import { useState, useEffect } from "react";

import CloseIcon from "@mui/icons-material/Close";
import Dialog from "@mui/material/Dialog";

import { TextModule } from "@/src/component/partial/__module__/TextModule";
import { ImgBgResponsive } from "@/src/component/base/ImgBgResponsive";
import { CardCarousel } from "@/src/component/partial/CardCarousel";
import { ProviderHooks } from "@/src/hook";
import type { StrapiComponentType } from "@/src/type";
import { LoadFromCDN } from "@/src/util";

import theme from "@/styles/scss/theme.module.scss";
import styles from "./CardScroll.module.scss";

const breakpoints = {
  1500: {
    slidesPerView: 1.6,
    spaceBetween: 24,
  },
  1025: {
    slidesPerView: 1.28,
    spaceBetween: 24,
  },
  750: {
    slidesPerView: 1.14,
    spaceBetween: 16,
  },
  200: {
    slidesPerView: 1.08,
    spaceBetween: 10,
  },
};

interface CardScrollDialogProps {
  cardScroll: StrapiComponentType.Section.CardScroll;
  openOnId?: number;
  openOnKey?: string;
  cardScrollSwiper: any;
  onClose?: () => void;
}

function CardScrollDialog({ cardScroll, openOnId, openOnKey, cardScrollSwiper, onClose }: CardScrollDialogProps) {
  const [openDialog, setOpenDialog] = useState<boolean>(false);
  const cms = ProviderHooks.useCMS();
  const quality = cms?.webConfig?.defaultImageQuality ?? 95;
  const cards = (cardScroll?.cards ?? []).length <= 3 ? [...cardScroll?.cards, ...cardScroll?.cards] : cardScroll?.cards;

  function handleDialogClose() {
    setOpenDialog(false);
  }

  useEffect(() => {
    if (typeof openOnKey !== "string") {
      return;
    }

    if (typeof openOnId === "number") {
      setOpenDialog(true);
      return;
    }

    setOpenDialog(false);
  }, [openOnId, openOnKey, cardScroll]);

  if (!cardScroll?.cards || typeof openOnId !== "number") {
    return <></>;
  }

  return (
    <Dialog
      onClose={handleDialogClose}
      open={openDialog}
      sx={{
        "& .MuiModal-backdrop": {
          backdropFilter: "blur(3px)",
        },
      }}
    >
      <div className={styles.dialogContainer}>
        <CardCarousel
          id={"CardScollDialog"}
          breakpoints={breakpoints}
          centerSlides={true}
          loop={true}
          classes={{
            container: styles.carouselContainer,
            buttonsContainer: styles.carouselButtons,
            buttonClasses: {
              prev: styles.carouselButtonPrev,
              next: styles.carouselButtonNext,
            },
          }}
          handleSlideChange={(swiper) => {
            if (typeof cardScrollSwiper?.activeIndex === "number" && !openDialog) {
              cardScrollSwiper.slideToLoop(swiper?.activeIndex, 0);
              if (typeof onClose === "function") {
                onClose();
              }
            }
          }}
          initialSlide={openOnId}
        >
          {(cards ?? []).map((card) => (
            <div
              key={card.id}
              className={styles.carouselCard}
            >
              <div
                className={styles.dialogExit}
                onClick={handleDialogClose}
              >
                <CloseIcon
                  fontSize="large"
                  sx={{ color: theme.primaryMain }}
                />
              </div>
              <div className={styles.inner}>
                <ImgBgResponsive
                  imageUrl={LoadFromCDN.nextGenFormat(LoadFromCDN.imageQuality(card?.backgroundImage.data?.attributes.url, quality))}
                  className={styles.carouselCardImg}
                />
                <div className={styles.carouselText}>
                  <div className={styles.textContainer}>
                    <TextModule
                      text={{
                        tag: "body1",
                        text: card.title,
                      }}
                      className={styles.cardTitle}
                    />
                    <TextModule
                      text={{
                        tag: "body1",
                        text: card.subtitle,
                      }}
                      className={styles.cardSubtitle}
                    />
                    <TextModule
                      text={{
                        tag: "body1",
                        text: card.underSubtitle,
                      }}
                      className={styles.underSubtitle}
                    />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </CardCarousel>
      </div>
    </Dialog>
  );
}

export default CardScrollDialog;
